import React, { CSSProperties, MouseEvent, useMemo, useRef } from 'react';
import { useTable, useExpanded, useRowSelect } from 'react-table';
import { CommonProps } from '../../lib';
import { DragDropContext, DroppableProvided, DraggableProvided } from 'react-beautiful-dnd';
import { useSticky } from 'react-table-sticky';

import MultiSelectionMenu from '../MultiSelectionMenu';
import { HeaderColumn, TableProps } from './types';
import { TableContainer, TableHeader, TableRow, TableWrapper } from './Table.styles';
import TableHeaderCell from './TableHeaderCell';
import { getSelectColumn } from './utils';
import { ConditionalDraggable } from '../utils/ConditionalDraggable';
import { ConditionalDroppable } from '../utils/ConditionalDroppable';

function Table<D extends object>({
  columns: cols,
  data = [],
  sortable = true,
  sort,
  onSortChange,
  onCellClick,
  onCellEnter,
  onCellLeave,
  onRowClick,
  onRowEnter,
  onRowLeave,
  getRowId,
  getSubRows,
  headerless = false,
  disabled = false,
  onDragEnd,
  droppableId = 'table',
  transparentBg = false,
  tableLayout,
  rowProps,
  initialState,
  autoResetExpanded = false,
  showSelection = false,
  selectionOptions,
  skipInternalDndContext = false,
  customOptions,
  ...props
}: TableProps<D> & CommonProps): JSX.Element | null {
  const columns = useMemo(
    () => [...getSelectColumn<D>(showSelection), ...cols],
    [showSelection, , cols],
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { selectedRowIds },
    toggleAllRowsSelected,
  } = useTable<D>(
    {
      columns,
      data,
      initialState,
      getRowId,
      getSubRows,
      autoResetExpanded,
      ...customOptions,
    },
    useExpanded,
    useSticky,
    useRowSelect,
  );

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const selectedIds = Object.keys(selectedRowIds);

  const getDraggableRowStyle = (isDragging: boolean, draggableStyle?: CSSProperties) => ({
    ...draggableStyle,
    ...(isDragging && tableWrapperRef.current
      ? {
          display: 'table',
          width: tableWrapperRef.current.clientWidth,
        }
      : {}),
  });

  if (!data?.length) {
    return null;
  }

  // If onDragEnd is provided or if we skip the dnd context, it means Table should be draggable
  const isDraggable = !!onDragEnd || skipInternalDndContext;

  const table = (
    <TableWrapper ref={tableWrapperRef} selectTable={showSelection}>
      <ConditionalDroppable droppableId={droppableId} isDraggable={isDraggable}>
        {(provided?: DroppableProvided) => (
          <TableContainer
            className="sticky"
            tableLayout={tableLayout}
            disabled={disabled}
            {...getTableProps()}
            transparentBg={transparentBg}
            ref={provided?.innerRef}
            hasSelection={!!selectedIds.length}
            selectTable={showSelection}
            {...props}
          >
            <colgroup>
              {columns.map(({ accessor, width, minWidth, maxWidth }, colIdx) => (
                <col
                  span={1}
                  key={accessor?.toString() ?? colIdx}
                  style={{ width, minWidth, maxWidth }}
                />
              ))}
            </colgroup>
            {!headerless && (
              <TableHeader>
                {headerGroups.map((headerGroup) => {
                  const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                  return (
                    <tr key={key} {...restHeaderGroupProps}>
                      {headerGroup.headers.map((header: HeaderColumn<D>, i) => (
                        <TableHeaderCell
                          key={header.getHeaderProps().key}
                          header={header}
                          columns={columns}
                          sort={sort}
                          sortable={sortable}
                          onSortChange={onSortChange}
                          index={i}
                        />
                      ))}
                    </tr>
                  );
                })}
              </TableHeader>
            )}
            <tbody {...getTableBodyProps()}>
              {rows.map((row, rowIndex) => {
                prepareRow(row);

                const { key, ...restRowProps } = row.getRowProps();

                return (
                  <ConditionalDraggable
                    key={key}
                    draggableId={row.id ?? rowIndex}
                    index={rowIndex}
                    isDraggable={isDraggable}
                  >
                    {(provided?: DraggableProvided, snapshot?: { isDragging?: boolean }) => (
                      <TableRow
                        isSelected={row.isSelected}
                        key={key}
                        {...(rowProps?.[rowIndex] ?? {})}
                        {...restRowProps}
                        {...(isDraggable && {
                          ref: provided?.innerRef,
                          ...provided?.draggableProps,
                          ...provided?.dragHandleProps,
                          className: snapshot?.isDragging ? 'dragging' : undefined,
                          style: getDraggableRowStyle(
                            snapshot?.isDragging || false,
                            provided?.draggableProps.style,
                          ),
                        })}
                        onMouseEnter={
                          !!onRowEnter
                            ? (e: MouseEvent<HTMLTableRowElement>) => {
                                onRowEnter(row, e);
                              }
                            : undefined
                        }
                        onMouseLeave={
                          !!onRowLeave
                            ? (e: MouseEvent<HTMLTableRowElement>) => {
                                onRowLeave(row, e);
                              }
                            : undefined
                        }
                        onClick={
                          !!onRowClick
                            ? (e) => {
                                onRowClick(row, e);
                              }
                            : undefined
                        }
                      >
                        {row.cells.map((cell, cellIdx) => {
                          const { key, style, ...restCellProps } = cell.getCellProps();

                          return (
                            <td
                              key={key}
                              style={{
                                minWidth: columns[cellIdx].minWidth,
                                maxWidth: columns[cellIdx].maxWidth,
                                width: columns[cellIdx].width,
                                ...style,
                              }}
                              className={
                                !!restCellProps.className || columns[cellIdx]?.showOnHover
                                  ? `${restCellProps.className ?? ''} ${
                                      columns[cellIdx]?.showOnHover ? 'showOnHover' : ''
                                    }`
                                  : undefined
                              }
                              onMouseEnter={
                                onCellEnter
                                  ? (e: React.MouseEvent<HTMLTableCellElement>) =>
                                      onCellEnter(cell, cellIdx, e)
                                  : undefined
                              }
                              onMouseLeave={
                                onCellLeave
                                  ? (e: React.MouseEvent<HTMLTableCellElement>) =>
                                      onCellLeave(cell, cellIdx, e)
                                  : undefined
                              }
                              onClick={
                                onCellClick
                                  ? (e: React.MouseEvent<HTMLTableCellElement>) =>
                                      onCellClick(cell, cellIdx, e)
                                  : undefined
                              }
                              data-testid={`table-${rowIndex}-${cellIdx}`}
                            >
                              {cell.render('Cell', { provided })}
                            </td>
                          );
                        })}
                      </TableRow>
                    )}
                  </ConditionalDraggable>
                );
              })}
              {provided?.placeholder}
            </tbody>
          </TableContainer>
        )}
      </ConditionalDroppable>
      {!!selectedIds.length && (
        <MultiSelectionMenu
          selectedIds={selectedIds}
          options={selectionOptions}
          onSelectAll={() => toggleAllRowsSelected(true)}
          onCancelSelection={() => toggleAllRowsSelected(false)}
        />
      )}
    </TableWrapper>
  );

  // if we don't want to skip the dnd context, we need to wrap the table in its own DragDropContext
  if (onDragEnd && !skipInternalDndContext)
    return <DragDropContext onDragEnd={onDragEnd}>{table}</DragDropContext>;

  return table;
}

export default Table;
